<template>
  <a-modal title="Attach Document" width="600px" :visible="visible" :destroy-on-close="true" @cancel="closeModal">
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-2 flex-column"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Template" prop="template_id">
        <search-template :value="form.template_id" @update="onSearchUpdate" />
      </a-form-model-item>

      <template v-if="template">
        <a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
          <a-input :disabled="true" :value="template.name" placeholder="My worker license" />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Details" prop="description">
          <a-textarea :disabled="true" :value="template.description" placeholder="My license to work on both sides" />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Type" prop="type">
          <a-input :disabled="true" :value="template.type.name" placeholder="My worker license" />
        </a-form-model-item>
        <a-form-model-item
          v-if="template.has_number"
          :colon="false"
          label-align="left"
          label="Document Number"
          prop="number"
        >
          <a-input v-model="form.number" placeholder="Document Number" />
        </a-form-model-item>
        <a-form-model-item
          v-if="template.can_expire"
          :colon="false"
          label-align="left"
          label="Expiration Date"
          prop="expiration_date"
        >
          <a-date-picker
            format="MM/DD/YYYY"
            placeholder="Expiration Date"
            v-model="form.expiration_date"
            valueFormat="YYYY-MM-DD"
          />
        </a-form-model-item>
        <a-form-model-item prop="files" :colon="false" label-align="left" label="Attachments">
          <document-attachments
            :with-title="false"
            :files="form.files"
            @save="saveAttachmentsIds"
          /> </a-form-model-item
      ></template>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal"> Cancel </a-button>
        <a-button icon="save" type="primary" @click="save" :loading="loading" :disabled="!template"> Save </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Button, FormModel, Modal, Input, notification, DatePicker } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import moment from 'moment-timezone'
import AttachedCreateSearch from './attached-create-search.vue'
import { deepCopy } from '@/utils/deep-copy'

const { mapActions: documentActions } = createNamespacedHelpers('document')

const initialForm = {
  name: undefined,
  description: undefined,
  type: undefined,
  number: undefined,
  expiration_date: undefined,
  files: [],
  template_id: undefined
}

const initialRules = {
  template_id: [
    {
      required: true,
      message: 'Template can not be empty',
      trigger: 'change'
    }
  ],
  number: [
    {
      required: true,
      message: 'Number can not be empty',
      trigger: 'change'
    }
  ],
  expiration_date: [
    {
      required: true,
      message: 'Expiration date can not be empty',
      trigger: 'change'
    }
  ],
  files: [
    {
      required: true,
      message: 'Files can not be empty',
      trigger: 'change',
      type: 'array'
    }
  ]
}

export default {
  components: {
    'a-button': Button,
    'a-textarea': Input.TextArea,
    'a-modal': Modal,
    'a-date-picker': DatePicker,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input': Input,
    'document-attachments': ShiftFormAttachments,
    'search-template': AttachedCreateSearch
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate_id: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,

      form: { ...initialForm },
      rules: {
        ...initialRules
      },

      types: [],

      attachmentsIds: [],

      template: null,
      serverDateFormat: this.$store.state.applicationState.serverDateFormat
    }
  },
  created() {
    this.rules.files = [
      {
        validator: this.validateAttachments,
        trigger: 'change'
      }
    ]
  },
  watch: {
    template: {
      handler() {
        this.form.expiration_date = undefined
        this.form.number = undefined
        this.attachmentsIds = []
      }
    }
  },
  methods: {
    ...documentActions(['createAssociateDocument']),

    closeModal() {
      this.$emit('update:visible', false)
      this.form = { ...initialForm }
      this.template = null
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    onSearchUpdate(template) {
      this.template = deepCopy(template)
      this.$set(this.form, 'template_id', this.template.id)
    },

    async saveAttachmentsIds(ids, flag) {
      this.attachmentsIds = ids

      if (this.$refs.form) {
        if (this.attachmentsIds.length > 0) {
          this.$refs.form.clearValidate(['files'])
        } else {
          this.$refs.form.validateField('files')
        }
      }

      if (flag !== 'remove') {
        return
      }

      if (!Array.isArray(this.form.files)) {
        this.form.files = []
        return
      }

      const attachmentIds = new Set(ids.map(item => item?.id).filter(id => typeof id === 'number'))

      this.form.files = this.form.files.filter(file => attachmentIds.has(file.id))
    },

    validateAttachments(rule, value, callback) {
      if (this.attachmentsIds.length > 0) {
        callback()
        return
      }

      callback(new Error('Files can not be empty'))
    },

    save() {
      const tempFiles = deepCopy(this.form.files)
      this.form.files = this.attachmentsIds

      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.form.files = tempFiles
          return
        }

        this.loading = true

        try {
          const payload = {
            document: {
              ...this.form,
              expiration_date: this.template.can_expire
                ? moment.utc(this.form.expiration_date).format(this.serverDateFormat)
                : undefined,
              number: this.template.has_number ? this.form.number : undefined,
              created_at: moment.utc().format(this.serverDateFormat),
              name: this.template.name,
              description: this.template.description,
              type: this.template.type.id,
              status: 'accepted'
            },
            associate_id: this.associate_id
          }

          const data = await this.createAssociateDocument(payload)

          this.showNotification('success', 'Success', 'Associate document has been created successfully.')

          this.closeModal()
          this.$emit('add', data)
        } catch (error) {
          this.showNotification('error', 'Error', error.message || error)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  width: 100%;
}

.ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin: 0;
}
</style>
